import React, { useEffect } from 'react';
import { TextField, MenuItem, FormHelperText, Checkbox, FormControlLabel, Stack, GridLegacy as Grid, Typography } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Gender, UserBase } from '../../types/user';
import dayjs from 'dayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { rtlTextFieldProps } from '../../utils/rtl';
import { formatBirthDateTime } from '../../services/time.service';
import RotatingImage from '../../components/RotatingImage';

// Helper component to handle side effects
interface FormikEffectProps {
  values: UserBase;
  isValid: boolean;
  onValidationChange: (isValid: boolean) => void;
  setUserData: (data: UserBase) => void;
}

const FormikEffect: React.FC<FormikEffectProps> = ({
  values,
  isValid,
  onValidationChange,
  setUserData
}) => {
  // Create ref at the top level of the component
  const prevValuesRef = React.useRef(values);

  // Update parent component about validation status
  useEffect(() => {
    onValidationChange(isValid);
  }, [isValid, onValidationChange]);

  // Update parent component with form values
  useEffect(() => {
    // Only update user data if it has actually changed to prevent unnecessary re-renders
    if (JSON.stringify(prevValuesRef.current) !== JSON.stringify(values)) {
      // Format the data before sending to parent
      const formattedValues = {
        ...values,
        birth_date: values.birth_date ? dayjs(values.birth_date).format('YYYY-MM-DD') : '',
        birth_time: values.birth_time ? values.birth_time : undefined
      };
      setUserData(formattedValues);
    }
    prevValuesRef.current = values;
  }, [values, setUserData]);

  return null;
};

interface UserDetailsStepProps {
  userData?: UserBase;
  setUserData: (data: UserBase) => void;
  onValidationChange: (isValid: boolean) => void;
}

const validationSchema = Yup.object({
  first_name: Yup.string().required('שם פרטי הוא שדה חובה'),
  last_name: Yup.string().required('שם משפחה הוא שדה חובה'),
  birth_date: Yup.mixed()
    .required('תאריך לידה הוא שדה חובה')
    .test('is-date', 'תאריך לא תקין', (value) => {
      if (!value) return false;
      return true; // DatePicker handles date validation
    })
    .test('is-adult', 'גיל המשתמש חייב להיות מעל 18', (value) => {
      if (!value) return false;
      const today = dayjs();
      const birthDate = dayjs(value.toString());
      const age = today.diff(birthDate, 'year');
      return age >= 18;
    }),
  gender: Yup.string().required('מגדר הוא שדה חובה'),
  mobile: Yup.string().when('confirm_marketing_content', {
    is: (val: boolean) => val === true,
    then: (schema) => schema.required('טלפון נייד הוא שדה חובה'),
    otherwise: (schema) => schema
  }),
  country_of_birth: Yup.string().required('ארץ לידה הוא שדה חובה'),
  city_of_birth: Yup.string().required('עיר לידה הוא שדה חובה'),
  email: Yup.string().email('כתובת אימייל אינה תקינה').when('confirm_marketing_content', {
    is: (val: boolean) => val === true,
    then: (schema) => schema.required('כתובת אימייל הוא שדה חובה'),
    otherwise: (schema) => schema
  }),
  password: Yup.string().when(['confirm_marketing_content', 'id'], {
    is: (confirmMarketing: boolean, id: string) => confirmMarketing === true && (!id || id === ''),
    then: (schema) => schema.required('סיסמה הוא שדה חובה'),
    otherwise: (schema) => schema
  }),
});

const emptyUser: UserBase & { password: string } = {
  id: '',
  first_name: '',
  last_name: '',
  birth_date: '',
  gender: Gender.FEMALE,
  mobile: '',
  country_of_birth: '',
  city_of_birth: '',
  birth_time: '',
  email: '',
  password: '',
  confirm_marketing_content: true,
  is_admin: false,
};

const UserDetailsStep: React.FC<UserDetailsStepProps> = ({
  userData,
  setUserData,
  onValidationChange
}) => {
  // Define a common style for form rows
  const formRowStyle = {
    width: '100%',
    gap: 2
  };

  // Create initial values, ensuring email and password are empty for new users
  const initialValues = userData ? {
    ...userData,
    password: '',
    // If user is not logged in (no id), ensure email is empty
    email: userData.id ? userData.email : ''
  } : emptyUser;

 

  return (
    <Formik
      initialValues={initialValues as UserBase & { password: string }}
      validationSchema={validationSchema}
      onSubmit={() => { }}
      validateOnMount
      validateOnChange
      validateOnBlur
    >
      {({ values, handleChange, handleBlur, isValid, touched, errors }) => {
        return (
          <Grid container spacing={3} sx={{ width: '100%' }}>
            {/* Form Section */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2} sx={{ width: '100%' }}>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                  <Typography variant="h6">פרטים אישיים</Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormikEffect
                    values={values}
                    isValid={isValid}
                    onValidationChange={onValidationChange}
                    setUserData={setUserData}
                  />
                  <Form dir="rtl">
                    <Stack spacing={2}>

                      {/* Name row */}
                      <Stack direction={{ xs: 'column', sm: 'row' }} sx={formRowStyle} >
                        <TextField
                          fullWidth
                          id="first_name"
                          name="first_name"
                          label="שם פרטי"
                          value={values.first_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.first_name && Boolean(errors.first_name)}
                          helperText={touched.first_name && errors.first_name}
                          required
                          {...rtlTextFieldProps}
                        />
                        <TextField
                          fullWidth
                          id="last_name"
                          name="last_name"
                          label="שם משפחה"
                          value={values.last_name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.last_name && Boolean(errors.last_name)}
                          helperText={touched.last_name && errors.last_name}
                          required
                          {...rtlTextFieldProps}
                        />
                      </Stack>

                      {/* Birth date and time row */}
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        sx={formRowStyle}
                      >
                        <DatePicker
                          value={values.birth_date ? dayjs(values.birth_date) : null}
                          format="DD/MM/YYYY"
                          onChange={(value: any) => handleChange({
                            target: { name: 'birth_date', value }
                          })}
                          slotProps={{
                            textField: {
                              id: "birth_date",
                              name: "birth_date",
                              label: "תאריך לידה",
                              error: touched.birth_date && Boolean(errors.birth_date),
                              helperText: touched.birth_date && Boolean(errors.birth_date) ? String(errors.birth_date) : undefined,
                              fullWidth: true,
                              onBlur: () => handleBlur({ target: { name: 'birth_date' } }),
                              required: true,
                              InputProps: {
                                sx: { textAlign: 'right', direction: 'rtl' }
                              },
                              InputLabelProps: {
                                sx: {
                                  right: 20,
                                  left: 'auto',
                                  transformOrigin: 'right top',
                                  position: 'absolute'
                                }
                              },
                            },
                            field: { clearable: true }
                          }}
                        />
                        <TimePicker
                          label="שעת לידה"
                          value={formatBirthDateTime(values.birth_time) ? dayjs(formatBirthDateTime(values.birth_time), 'HH:mm') : null}
                          onChange={(newValue) => {
                            const timeString = newValue ? newValue.format('HH:mm') : '';
                            handleChange({
                              target: {
                                name: 'birth_time',
                                value: timeString
                              }
                            });
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              id: "birth_time",
                              name: "birth_time",
                              onBlur: handleBlur,
                              error: touched.birth_time && Boolean(errors.birth_time),
                              helperText: touched.birth_time && errors.birth_time,
                              InputProps: {
                                sx: { textAlign: 'right', direction: 'rtl' }
                              },
                              InputLabelProps: {
                                sx: {
                                  right: 20,
                                  left: 'auto',
                                  transformOrigin: 'right top',
                                  position: 'absolute'
                                }
                              },
                            }
                          }}
                          ampm={false}
                          minutesStep={5}
                        />
                      </Stack>

                      {/* Gender row */}
                      <Stack direction={{ xs: 'column', sm: 'row' }} sx={formRowStyle} >
                        <TextField
                          fullWidth
                          select
                          id="gender"
                          name="gender"
                          label="מגדר"
                          value={values.gender}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.gender && Boolean(errors.gender)}
                          helperText={touched.gender && errors.gender}
                          required
                          InputProps={{
                            sx: { textAlign: 'right', direction: 'rtl' }
                          }}
                          InputLabelProps={{
                            sx: {
                              right: 20,
                              left: 'auto',
                              transformOrigin: 'right top',
                              position: 'absolute'
                            }
                          }}
                        >
                          <MenuItem value={Gender.MALE}>זכר</MenuItem>
                          <MenuItem value={Gender.FEMALE}>נקבה</MenuItem>
                        </TextField>
                      </Stack>

                      {/* Country of birth and city of birth row */}
                      <Stack direction={{ xs: 'column', sm: 'row' }} sx={formRowStyle}>
                        <TextField
                          fullWidth
                          id="country_of_birth"
                          name="country_of_birth"
                          label="ארץ לידה"
                          value={values.country_of_birth}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.country_of_birth && Boolean(errors.country_of_birth)}
                          helperText={touched.country_of_birth && errors.country_of_birth}
                          required
                          {...rtlTextFieldProps}
                        />
                        <TextField
                          fullWidth
                          id="city_of_birth"
                          name="city_of_birth"
                          label="עיר לידה"
                          value={values.city_of_birth}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.city_of_birth && Boolean(errors.city_of_birth)}
                          helperText={touched.city_of_birth && errors.city_of_birth}
                          required
                          {...rtlTextFieldProps}
                        />
                      </Stack>

                      {/* Confirm marketing content row */}
                      <Stack direction={{ xs: 'column', sm: 'row' }} sx={formRowStyle} >
                        <FormControlLabel
                          control={
                            <Checkbox
                              disabled={!!values.id}
                              id="confirm_marketing_content"
                              name="confirm_marketing_content"
                              checked={values.confirm_marketing_content}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          }
                          label="האם מעניין אותך לקבל עדכונים אישיים?"
                          sx={{ width: '100%', justifyContent: 'flex-end' }}
                          labelPlacement="start"
                        />
                        {touched.confirm_marketing_content && errors.confirm_marketing_content && (
                          <FormHelperText error sx={{ textAlign: 'right' }}>{errors.confirm_marketing_content}</FormHelperText>
                        )}
                      </Stack>

                      {/* Mobile and email row */}
                      {values.confirm_marketing_content && <Stack direction={{ xs: 'column', sm: 'row' }} sx={formRowStyle}>
                        <TextField
                          disabled={!!values.id}
                          fullWidth
                          id="mobile"
                          name="mobile"
                          label="טלפון נייד"
                          value={values.mobile}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.mobile && Boolean(errors.mobile)}
                          helperText={touched.mobile && errors.mobile}
                          required
                          autoComplete="off"
                          inputProps={{
                            autoComplete: "off"
                          }}
                          {...rtlTextFieldProps}
                        />
                        <TextField
                          disabled={!!values.id}
                          fullWidth
                          id="email"
                          name="email"
                          label="אימייל"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          type="email"
                          autoComplete="off"
                          inputProps={{
                            pattern: "[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
                            style: { textAlign: 'right', direction: 'rtl' },
                            autoComplete: "off"
                          }}
                          {...rtlTextFieldProps}
                        />
                      </Stack>
                      }
                      {values.confirm_marketing_content && !values.id && <Stack direction={{ xs: 'column', sm: 'row' }} sx={formRowStyle} maxWidth="50%">
                        <TextField
                          name="password"
                          label="סיסמה"
                          type="password"
                          fullWidth
                          value={values.password}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                          autoComplete="new-password"
                          inputProps={{
                            autoComplete: "new-password"
                          }}
                          {...rtlTextFieldProps}
                        />
                      </Stack>
                      }

                    </Stack>
                  </Form >
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 10 }}>
              <RotatingImage />
            </Grid>
          </Grid>
        );
      }}
    </Formik >
  );
};


export default UserDetailsStep; 